import { useAppSelector } from '@frontend/common';
import { CheckboxInput, SelectInputV2 } from '@frontend/components/basic-forms';
import { ModalType, useModals } from '@frontend/modal';
import { ListInfo, changePageSize, changeSorting, listStore } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import type { Column } from '@tanstack/react-table';
import { ColumnDef, Table as ReactTable, Row, VisibilityState, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { THeader } from './headers/THeader';
import PaginationButtonNavigation from './pagination/pagination-button-navigation';
import { TRow } from './rows/TRow';

export interface TableProps {
    dispatch: ThunkDispatch<any, any, Action>;
    listId: string;
    useStaticListId?: boolean;
    headerClassName?: string;
    headerItemClassName?: string;
    rowClassName?: string;
    customDivclassName?: string;
    customRowClassName?: (row: Row<object>) => string | undefined;

    selectEnabled?: boolean;
    expandEnabled?: boolean;
    selectedItemsCallback?: (objects: any[]) => void;
    columns: ColumnDef<any>[];
    data: any[];
    autoResetPage?: boolean;
    next?: string | null | undefined;
    count?: number | undefined;
    onSelectElement?: (object: any) => void;
    RowElement: (props: any) => JSX.Element;
    renderSubComponent?: (props: { row: any }) => React.ReactElement;
    pagination?: {
        page?: (value: number) => void;
        initialPageSize?: number;
        hidePagination?: boolean;
        hideResults?: boolean;
        hidePageSize?: boolean;

        allowPageSizeSelect?: boolean;
        allowPageJumpInput?: { bottom?: boolean; top?: boolean };
        allowPageButtonNavigation?: {
            bottom?: boolean;
            bottomProps?: Partial<Omit<React.ComponentProps<typeof PaginationButtonNavigation>, 'dispatch' | 'listStoreId'>>;
            top?: boolean;
            topProps?: Partial<Omit<React.ComponentProps<typeof PaginationButtonNavigation>, 'dispatch' | 'listStoreId'>>;
        };
    };
    id?: string;
    hideColumns?: string[];
    sortingColumns?: string[];
    /**
     * @description Columns where the sort direction should be reversed when sent to the backend.
     * This is useful for computed values where ascending/descending needs to be inverted.
     */
    reverseSortingColumns?: string[];
    showCount?: boolean;
    includeDeletedCallback?: (value: boolean) => void;
    optionsModalOpen?: boolean;
    setOptionsModalOpen?: (value: boolean) => void;
    customFooter?: React.ReactNode | ((table: ReactTable<any>) => React.ReactNode);
    maxHeight?: boolean;
    defaultHeight?: boolean;
    hideFooter?: boolean;
    hideFilter?: boolean;
    isLoading?: boolean;
}

export function Table(props: TableProps) {
    const store = useAppSelector(useSelector, listStore);
    const list: ListInfo | undefined = store[props.listId];

    const tableSorting = useMemo(() => {
        if (!list?.sorting) return [];
        if (!props.reverseSortingColumns?.length) return list.sorting;
        return list.sorting.map((sort) => (props.reverseSortingColumns!.includes(sort.id) ? { ...sort, desc: !sort.desc } : sort));
    }, [list?.sorting, props.reverseSortingColumns]);
    const possiblePageSizes = [10, 25, 50, 100, 150];
    const totalCount = list?.count ?? props.count ?? props.data.length;
    const pageSize = list?.pageSize ?? props.pagination?.initialPageSize ?? 25;
    const pageCount = totalCount ? Math.ceil(totalCount / pageSize) : 0;
    type TableOptions = {
        columns?: VisibilityState;
        order?: string[];
    };
    const { openModal, closeTopModal, closeModal, updateModal } = useModals();

    /**
     * Table options for the current list.
     * storing these in the localstorage is not a viable option
     * we encountered the issue that the localstorage was full and this made the entire app crash
     */
    // const [tableOptions, setTableOptions] = props.useStaticListId
    //     ? useLocalStorageState<TableOptions | undefined>(`table-options-${props.listId}`, {})
    //     : [undefined, () => {}];

    const columnIds = props.columns.map((col) => col.id).join(',');

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => (props.listId ? {} : {}));

    const optionsModalIdRef = useRef<string | null>(null);

    useEffect(() => {
        // if (!props.listId || !tableOptions || Object.keys(tableOptions).length === 0 || Object.keys(columnVisibility).length > 0) return;
        // if (!tableOptions.columns || Object.keys(tableOptions.columns).length === 0)
        setColumnVisibility(props.columns.reduce((acc, col) => ({ ...acc, [col.id!]: true }), {}));
        // else setColumnVisibility(tableOptions.columns);
    }, [props.listId, columnIds, props.columns]);

    // useEffect(() => {
    //     if (!props.listId || Object.keys(columnVisibility).length === 0) return;
    //     setTableOptions({
    //         columns: columnVisibility,
    //         order: props.columns.map((col) => col.id!)
    //     });
    // }, [props.listId, columnVisibility]);

    useEffect(() => {
        if (props.pagination?.initialPageSize !== undefined && !possiblePageSizes.includes(props.pagination.initialPageSize)) {
            possiblePageSizes.push(props.pagination.initialPageSize);
            possiblePageSizes.sort((a, b) => a - b);
            props.dispatch(changePageSize({ listId: props.listId, pageSize: props.pagination.initialPageSize }));
        }
    }, [props.pagination?.initialPageSize, props.dispatch, props.listId]);

    const tableInstance = useReactTable({
        columns: props.columns,
        data: props.data,
        autoResetPageIndex: props.autoResetPage ? props.autoResetPage : false,
        manualPagination: true,
        pageCount: pageCount,
        manualSorting: true,
        autoResetExpanded: false,
        getSubRows: (row: any) => row?.children,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            columnVisibility,
            sorting: tableSorting
        },
        onSortingChange: (updater) => {
            const newSorting = typeof updater === 'function' ? updater(tableSorting) : updater;
            if (!props.reverseSortingColumns?.length) {
                props.dispatch(changeSorting({ listId: props.listId, sorting: newSorting }));
                return;
            }
            const adjustedSorting = newSorting.map((sort) => (props.reverseSortingColumns!.includes(sort.id) ? { ...sort, desc: !sort.desc } : sort));
            props.dispatch(changeSorting({ listId: props.listId, sorting: adjustedSorting }));
        },
        onColumnVisibilityChange: setColumnVisibility
    });

    useEffect(() => {
        if (props.selectEnabled && props.selectedItemsCallback) {
            props.selectedItemsCallback(tableInstance.getSelectedRowModel().flatRows.map((r) => r.original));
        }
    }, [props.selectEnabled, props.selectedItemsCallback, tableInstance]);

    useEffect(() => {
        if (!props.hideColumns || props.hideColumns.length === 0) return;
        tableInstance.getAllColumns().forEach((col) => {
            col.toggleVisibility(!props.hideColumns?.includes(col.id));
        });
    }, [props.hideColumns, tableInstance]);

    const hidePagination = props.pagination?.hidePagination ?? totalCount <= pageSize;

    const mainHeader = document.getElementById('main-platform-header');
    const breadcrumbs = document.getElementById('main-platform-breadcrumbs');
    const subnav = document.getElementById('subnav');
    const tabFilter = document.getElementById('tab-filter');
    const tableFilter = document.getElementById('table-filter');
    const adminTableHeader = document.getElementById('admin-table-header');

    // Use a smaller margin for mobile screens
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    const extraMargin = isMobile ? 190 : 120;
    const height =
        (mainHeader?.clientHeight || 0) +
        (breadcrumbs?.clientHeight || 0) +
        (subnav?.clientHeight || 0) +
        (tabFilter?.clientHeight || 0) +
        (tableFilter?.clientHeight || 0) +
        (adminTableHeader?.clientHeight || 0) +
        extraMargin;

    const memoizedModalRenderFunction = useCallback(() => {
        return (
            <TableOptionsModal
                onClick={() => tableInstance.resetColumnVisibility(true)}
                columns={tableInstance.getAllLeafColumns()}
            />
        );
    }, [tableInstance]);

    useEffect(() => {
        if (props.optionsModalOpen && optionsModalIdRef.current === null) {
            const id = openModal(
                'table-options-modal',
                memoizedModalRenderFunction,
                { handleCloseCallback: () => props.setOptionsModalOpen && props.setOptionsModalOpen(false) },
                { customWidth: 20, type: ModalType.PANE }
            );
            optionsModalIdRef.current = id;
        } else if (!props.optionsModalOpen && optionsModalIdRef.current !== null) {
            closeModal(optionsModalIdRef.current);
            optionsModalIdRef.current = null;
        }
    }, [props.optionsModalOpen, openModal, closeModal, props.setOptionsModalOpen, memoizedModalRenderFunction]);
    useEffect(() => {
        if (optionsModalIdRef.current !== null && props.optionsModalOpen) {
            updateModal(optionsModalIdRef.current, memoizedModalRenderFunction);
        }
    }, [columnVisibility, optionsModalIdRef.current, props.optionsModalOpen, updateModal, memoizedModalRenderFunction]);

    const style = props.maxHeight ? { maxHeight: `calc(100dvh - ${height}px)` } : { height: `calc(100dvh - ${height}px)` };
    return (
        <div
            data-testid='table-container'
            id={props.id}
            style={props.defaultHeight ? undefined : style}
            className={`relative flex max-w-full flex-col justify-between overflow-auto`}>
            <table
                data-testid={props.id}
                className='w-full border-collapse text-left'>
                <thead className='outline-border-default z-20 h-12 outline-1'>
                    {tableInstance.getHeaderGroups().map((headerGroup, index) => (
                        <THeader
                            className={props.headerItemClassName}
                            customDivClassName={props.customDivclassName}
                            hideFilter={props.hideFilter}
                            key={index + '_' + headerGroup.id}
                            headerGroup={headerGroup}
                            sortingColumns={props.sortingColumns}
                            tableId={props.listId}
                            dispatch={props.dispatch}
                        />
                    ))}
                </thead>

                <tbody>
                    {props.data.length === 0 && (
                        <tr>
                            <td
                                className='p-3'
                                colSpan={props.columns.length}>
                                <div className='text-primary ms-2'>
                                    <FormattedMessage
                                        id='table.no-data'
                                        description='The label we display when there are no results in a table'
                                        defaultMessage='No data to display'
                                    />
                                </div>
                            </td>
                        </tr>
                    )}
                    {tableInstance.getRowModel().rows.map((row) => (
                        <TRow
                            className={props.customRowClassName ? props.customRowClassName(row) : props.rowClassName}
                            key={row.id}
                            row={row}
                            selectEnabled={props.selectEnabled}
                            onClick={props.onSelectElement}
                            RowElement={props.RowElement}
                        />
                    ))}
                </tbody>
            </table>
            {!props.hideFooter && (
                <div
                    data-testid={props.id + '-footer'}
                    className='border-border-default bg-background-surface sticky bottom-[-1px] left-0 z-5 inline-flex w-full items-center justify-between self-stretch rounded-br-2xl rounded-bl-2xl border-t p-4'>
                    {props.customFooter ? (
                        typeof props.customFooter === 'function' ? (
                            props.customFooter(tableInstance)
                        ) : (
                            props.customFooter
                        )
                    ) : (
                        <>
                            <div className='flex items-center justify-start gap-4'>
                                {!props.pagination?.hideResults && (
                                    <div className='flex items-center justify-start gap-2.5'>
                                        <div className='text-foreground-default flex justify-start gap-1 text-lg font-semibold'>
                                            <span>{totalCount}</span>
                                            <FormattedMessage
                                                id='table.results'
                                                description='The label for results'
                                                defaultMessage='results'
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            {!hidePagination &&
                                pageCount > 1 &&
                                (!props.pagination ||
                                    !props.pagination.allowPageButtonNavigation ||
                                    props.pagination.allowPageButtonNavigation.bottom !== false) && (
                                    <PaginationButtonNavigation
                                        dispatch={props.dispatch}
                                        listStoreId={props.listId}
                                        id={props.id + '-bottom'}
                                        {...props.pagination?.allowPageButtonNavigation?.bottomProps}
                                    />
                                )}
                            <div className='inline-flex w-1/12 items-center justify-end gap-3 sm:w-1/3 md:w-1/4 lg:w-1/5 xl:w-1/6 2xl:w-1/8'>
                                {!props.pagination?.hidePageSize && (
                                    <>
                                        <span className='text-brand-muted font-medium text-nowrap'>
                                            <FormattedMessage
                                                id='table.pageSize'
                                                description='The page size in the default table message.'
                                                defaultMessage='Page size'
                                            />
                                        </span>
                                        <SelectInputV2<number>
                                            options={possiblePageSizes.map((size) => ({ label: size.toString(), value: size.toString() }))}
                                            value={list?.pageSize?.toString()}
                                            onSelect={(value) => {
                                                props.dispatch(changePageSize({ listId: props.listId, pageSize: Number(value?.value) }));
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export const TableOptionsModal = (props: { onClick: (bool: boolean) => void; columns: Column<any, unknown>[] }) => {
    return (
        <div className='p-4'>
            <h2 className='mb-4 text-lg font-semibold'>Table Options</h2>
            <div className='mb-4 flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Columns</span>
                <button
                    onClick={() => props.onClick(true)}
                    className='text-primary text-sm hover:underline'>
                    Reset
                </button>
            </div>
            <div className='flex grow flex-col justify-between'>
                {props.columns.map((col) => {
                    return (
                        <div
                            key={col.id}
                            className='mb-2'>
                            <CheckboxInput
                                id={col.id}
                                value={col.getIsVisible()}
                                onChange={col.toggleVisibility}
                                disabled={!col.getCanHide()}
                                label={col.id.replace(/_/g, ' ')}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
